/**
 * VELLUM Native Cart Drawer + Storefront AJAX API helpers
 * Endpoints: /cart.js, /cart/add.js, /cart/change.js, /cart/update.js
 */
(function () {
  'use strict';

  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const moneyFormat =
    (window.theme && window.theme.moneyFormat) ||
    (window.Shopify && window.Shopify.money_format) ||
    '{{amount}} TL';

  /* --------------------------------------------------------------------------
     Money helpers
     -------------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0) / 100;
    const fmt = format || moneyFormat;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }

    const formatted = value.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString('tr-TR'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted);
  }

  /* --------------------------------------------------------------------------
     Cart API
     -------------------------------------------------------------------------- */
  async function fetchCart() {
    const res = await fetch(`${ROOT()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Sepet yüklenemedi');
    return res.json();
  }

  /**
   * Add one or many items.
   * @param {Array|{id:number,quantity:number,properties?:object}} items
   */
  async function addItems(items) {
    const payload = Array.isArray(items) ? { items } : { items: [items] };
    const res = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.description || data.message || 'Sepete eklenemedi');
      err.payload = data;
      throw err;
    }
    return data;
  }

  async function changeItem({ line, quantity, id, key }) {
    const body = { quantity };
    if (line != null) body.line = line;
    else if (key) body.id = key;
    else if (id) body.id = id;

    const res = await fetch(`${ROOT()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Sepet güncellenemedi');
    return data;
  }

  async function updateCart(updates) {
    const res = await fetch(`${ROOT()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Sepet güncellenemedi');
    return data;
  }

  /* --------------------------------------------------------------------------
     Drawer UI
     -------------------------------------------------------------------------- */
  class VellumCartDrawer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-vellum-drawer-panel]');
      this.overlay = root.querySelector('[data-vellum-drawer-overlay]');
      this.body = root.querySelector('[data-vellum-drawer-body]');
      this.countEls = document.querySelectorAll('[data-vellum-cart-count]');
      this.threshold = Number(root.dataset.freeShippingThreshold || 79000); // cents
      this.upsellVariantId = Number(root.dataset.upsellVariantId || 0);
      this.upsellProductId = Number(root.dataset.upsellProductId || 0);
      this.upsellPrice = Number(root.dataset.upsellPrice || 29900);
      this.upsellTitle = root.dataset.upsellTitle || 'Steampunk Deri Defter';
      this.upsellImage = root.dataset.upsellImage || '';
      this.moneyFormat = root.dataset.moneyFormat || moneyFormat;
      this.isOpen = false;
      this.cart = null;
      this.bind();
    }

    bind() {
      this.root.querySelectorAll('[data-vellum-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      document.addEventListener(
        'click',
        (e) => {
          const opener = e.target.closest('[data-vellum-cart-open]');
          if (opener) {
            // Prefer full cart page when href points to /cart
            const href = opener.getAttribute('href') || '';
            if (href.includes('/cart')) return;
            e.preventDefault();
            e.stopPropagation();
            this.open();
          }
        },
        true
      );

      // Delegate qty / remove / upsell inside body
      this.body.addEventListener('click', (e) => this.onBodyClick(e));

      // Listen for PDP / global cart events
      document.addEventListener('vellum:cart:refresh', (e) => {
        const cart = e.detail && e.detail.cart;
        if (cart) {
          this.cart = cart;
          this.render(cart);
          this.updateCounts(cart);
        } else {
          this.refresh();
        }
      });

      document.addEventListener('vellum:cart:open', () => {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        window.location.href = cartUrl;
      });
    }

    async open() {
      await this.refresh();
      this.isOpen = true;
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('vellum-scroll-lock');
      const closeBtn = this.root.querySelector('[data-vellum-drawer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    close() {
      this.isOpen = false;
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('vellum-scroll-lock');
    }

    updateCounts(cart) {
      const count = cart.item_count || 0;
      this.countEls.forEach((el) => {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
    }

    async refresh() {
      try {
        this.cart = await fetchCart();
        this.render(this.cart);
        this.updateCounts(this.cart);
        return this.cart;
      } catch (err) {
        console.error('[VELLUM] cart refresh', err);
        this.body.innerHTML = `<p class="vellum-drawer__empty">Sepet yüklenemedi. Lütfen tekrar deneyin.</p>`;
      }
    }

    hasUpsellInCart(cart) {
      if (!this.upsellVariantId && !this.upsellProductId) return true;
      return (cart.items || []).some((item) => {
        if (this.upsellVariantId && item.id === this.upsellVariantId) return true;
        if (this.upsellProductId && item.product_id === this.upsellProductId) return true;
        return false;
      });
    }

    shippingProgress(cart) {
      const total = cart.items_subtotal_price || cart.total_price || 0;
      const remaining = Math.max(this.threshold - total, 0);
      const pct = Math.min(100, Math.round((total / this.threshold) * 100));
      const unlocked = remaining === 0;

      const message = unlocked
        ? `<strong>Kargo bedava!</strong> Siparişiniz ücretsiz kargo eşiğini geçti.`
        : `<strong>${formatMoney(remaining, this.moneyFormat)}</strong> daha ekleyin — ${formatMoney(
            this.threshold,
            this.moneyFormat
          )} üzeri kargo bedava!`;

      return `
        <div class="vellum-ship" data-vellum-shipping>
          <p class="vellum-ship__text">${message}</p>
          <div class="vellum-ship__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}">
            <div class="vellum-ship__bar" style="width:${pct}%"></div>
          </div>
        </div>
      `;
    }

    renderUpsell(cart) {
      if (this.hasUpsellInCart(cart) || !this.upsellVariantId) return '';

      const img = this.upsellImage
        ? `<img src="${this.upsellImage}" alt="${this.escape(this.upsellTitle)}" width="112" height="112" loading="lazy">`
        : '';

      return `
        <aside class="vellum-drawer-upsell" data-vellum-drawer-upsell>
          <h3 class="vellum-drawer-upsell__title">Siparişine Steampunk Deri Defteri Ekle!</h3>
          <div class="vellum-drawer-upsell__row">
            <div class="vellum-drawer-upsell__media">${img}</div>
            <div>
              <p class="vellum-drawer-upsell__price">Sadece ${formatMoney(this.upsellPrice, this.moneyFormat)}</p>
              <button type="button" class="vellum-btn vellum-btn--ghost" data-vellum-upsell-add>
                + Tek Tıkla Ekle
              </button>
            </div>
          </div>
        </aside>
      `;
    }

    renderItems(cart) {
      if (!cart.items || !cart.items.length) {
        return `<div class="vellum-drawer__empty"><p>Sepetiniz boş.</p><p>VELLUM yazı ritüelinizi keşfetmeye başlayın.</p></div>`;
      }

      return cart.items
        .map((item, index) => {
          const line = index + 1;
          const img = item.image
            ? `<img src="${item.image}" alt="${this.escape(item.product_title)}" width="144" height="144" loading="lazy">`
            : '';

          const props = item.properties
            ? Object.keys(item.properties)
                .filter((k) => k && !k.startsWith('_') && item.properties[k])
                .map((k) => `${this.escape(k)}: ${this.escape(String(item.properties[k]))}`)
                .join(' · ')
            : '';

          return `
            <article class="vellum-cart-item" data-line="${line}" data-key="${this.escape(item.key)}">
              <div class="vellum-cart-item__media">${img}</div>
              <div>
                <h3 class="vellum-cart-item__title">${this.escape(item.product_title)}</h3>
                ${props ? `<p class="vellum-cart-item__props">${props}</p>` : ''}
                <div class="vellum-cart-item__row">
                  <div class="vellum-qty">
                    <button type="button" class="vellum-qty__btn" data-vellum-qty="-1" aria-label="Azalt">−</button>
                    <span class="vellum-qty__value">${item.quantity}</span>
                    <button type="button" class="vellum-qty__btn" data-vellum-qty="1" aria-label="Artır">+</button>
                  </div>
                  <span class="vellum-cart-item__price">${formatMoney(item.final_line_price, this.moneyFormat)}</span>
                </div>
                <button type="button" class="vellum-cart-item__remove" data-vellum-remove>Kaldır</button>
              </div>
            </article>
          `;
        })
        .join('');
    }

    render(cart) {
      const itemsHtml = this.renderItems(cart);
      const shipHtml = cart.item_count ? this.shippingProgress(cart) : '';
      const upsellHtml = cart.item_count ? this.renderUpsell(cart) : '';
      const subtotal = formatMoney(cart.items_subtotal_price || cart.total_price || 0, this.moneyFormat);

      this.body.innerHTML = `
        ${shipHtml}
        <div data-vellum-cart-items>${itemsHtml}</div>
        ${upsellHtml}
      `;

      const subtotalEl = this.root.querySelector('[data-vellum-subtotal]');
      if (subtotalEl) subtotalEl.textContent = subtotal;

      const footer = this.root.querySelector('[data-vellum-drawer-footer]');
      if (footer) footer.hidden = !cart.item_count;
    }

    async onBodyClick(e) {
      const upsellBtn = e.target.closest('[data-vellum-upsell-add]');
      if (upsellBtn) {
        e.preventDefault();
        upsellBtn.disabled = true;
        try {
          const penQty = (this.cart && this.cart.items
            ? this.cart.items.reduce((sum, item) => {
                const handle = item.handle || '';
                if (handle.includes('divit-kalem') || handle.includes('divit')) {
                  return sum + Number(item.quantity || 0);
                }
                return sum;
              }, 0)
            : 0) || 1;
          await addItems({
            id: this.upsellVariantId,
            quantity: Math.max(1, penQty),
            properties: {
              _vellum_upsell: 'cart-drawer',
              _vellum_match_sets: '1',
            },
          });
          const cart = await fetchCart();
          this.cart = cart;
          this.render(cart);
          this.updateCounts(cart);
          document.dispatchEvent(new CustomEvent('vellum:cart:updated', { detail: { cart } }));
        } catch (err) {
          console.error(err);
          alert(err.message || 'Ürün eklenemedi');
        } finally {
          upsellBtn.disabled = false;
        }
        return;
      }

      const item = e.target.closest('.vellum-cart-item');
      if (!item) return;

      const line = Number(item.dataset.line);
      const key = item.dataset.key;
      const qtyBtn = e.target.closest('[data-vellum-qty]');
      const removeBtn = e.target.closest('[data-vellum-remove]');

      try {
        if (removeBtn) {
          await changeItem({ line, key, quantity: 0 });
        } else if (qtyBtn) {
          const delta = Number(qtyBtn.dataset.vellumQty);
          const current = Number(item.querySelector('.vellum-qty__value').textContent);
          const next = Math.max(0, current + delta);
          await changeItem({ line, key, quantity: next });
        } else {
          return;
        }
        const cart = await fetchCart();
        this.cart = cart;
        this.render(cart);
        this.updateCounts(cart);
        document.dispatchEvent(new CustomEvent('vellum:cart:updated', { detail: { cart } }));
      } catch (err) {
        console.error(err);
        alert(err.message || 'Sepet güncellenemedi');
      }
    }

    escape(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  /* --------------------------------------------------------------------------
     PDP: Volume tiers + multi-item add (cross-sell)
     -------------------------------------------------------------------------- */
  class VellumPdpBuyBox {
    constructor(root) {
      this.root = root;
      this.form = document.querySelector(root.dataset.formSelector || 'form[data-type="add-to-cart-form"]');
      this.priceEl = root.querySelector('[data-vellum-live-price]');
      this.compareEl = root.querySelector('[data-vellum-live-compare]');
      this.qtyInput =
        (this.form && this.form.querySelector('[name="quantity"]')) ||
        document.querySelector('quantity-input .quantity__input, input[name="quantity"]');
      this.upsellCheckbox = root.querySelector('[data-vellum-upsell-checkbox]');
      this.unitPrice = Number(root.dataset.unitPrice || 0);
      this.discountCodes = {
        2: root.dataset.discountCode2 || '',
        3: root.dataset.discountCode3 || '',
      };
      this.bind();
      this.syncFromSelected();
    }

    bind() {
      this.root.querySelectorAll('[data-vellum-tier]').forEach((input) => {
        input.addEventListener('change', () => this.syncFromSelected());
      });

      if (!this.form) return;

      this.form.addEventListener(
        'submit',
        (e) => {
          // Intercept native submit → multi-item /cart/add.js
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleAddToCart();
        },
        true
      );
    }

    getSelectedTier() {
      const selected = this.root.querySelector('[data-vellum-tier]:checked');
      if (!selected) {
        return { qty: 1, discount: 0, label: '1 Set' };
      }
      return {
        qty: Number(selected.dataset.qty || 1),
        discount: Number(selected.dataset.discount || 0),
        label: selected.dataset.label || '',
        code: selected.dataset.discountCode || '',
      };
    }

    calcTotals(qty, discountPct) {
      const unit = this.unitPrice;
      const compare = unit * qty;
      const total = Math.round(compare * (1 - discountPct / 100));
      return { compare, total };
    }

    syncFromSelected() {
      const tier = this.getSelectedTier();
      const { compare, total } = this.calcTotals(tier.qty, tier.discount);

      if (this.qtyInput) {
        this.qtyInput.value = String(tier.qty);
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(total);
        this.priceEl.dataset.cents = String(total);
      }
      if (this.compareEl) {
        if (tier.discount > 0) {
          this.compareEl.hidden = false;
          this.compareEl.textContent = formatMoney(compare);
        } else {
          this.compareEl.hidden = true;
        }
      }

      this.syncUpsellCopy(tier.qty);
      this.ensurePropertyInputs(tier);
    }

    syncUpsellCopy(qty) {
      const box = this.root.querySelector('[data-vellum-cross-sell]');
      if (!box) return;
      const offerCents = Number(
        (this.upsellCheckbox && this.upsellCheckbox.dataset.offerCents) ||
          this.root.dataset.journalOfferCents ||
          29900
      );
      const compareCents = Number(box.dataset.compareCents || offerCents);
      const n = Math.max(1, Number(qty) || 1);
      const titleEl = box.querySelector('[data-vellum-upsell-title]');
      const priceEl = box.querySelector('[data-vellum-upsell-price]');
      const compareEl = box.querySelector('[data-vellum-upsell-compare]');
      const noteEl = box.querySelector('[data-vellum-upsell-note]');

      if (titleEl) {
        titleEl.textContent =
          n > 1 ? `Özel deri defteri ekle (${n} adet)` : 'Özel deri defteri ekle';
      }
      if (priceEl) priceEl.textContent = `+${formatMoney(offerCents * n)}`;
      if (compareEl) compareEl.textContent = formatMoney(compareCents * n);
      if (noteEl) {
        noteEl.hidden = false;
        noteEl.textContent =
          n > 1
            ? `Seçili ${n} set için ${n} defter eklenir · adet ${formatMoney(offerCents)}`
            : `Set başına ${formatMoney(offerCents)}`;
      }
    }

    ensurePropertyInputs(tier) {
      if (!this.form) return;
      const fields = {
        properties: {
          _vellum_paket: tier.label,
          _vellum_qty: String(tier.qty),
          _vellum_discount_pct: String(tier.discount),
        },
      };

      Object.entries(fields.properties).forEach(([key, value]) => {
        const name = `properties[${key}]`;
        let input = this.form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          this.form.appendChild(input);
        }
        input.value = value;
      });

      // Remove legacy visible Bundle property if present
      const legacy = this.form.querySelector('input[name="properties[Bundle]"]');
      if (legacy) legacy.remove();
    }

    getVariantId() {
      const input =
        (this.form && this.form.querySelector('[name="id"]')) ||
        document.querySelector('.product-variant-id, [name="id"]');
      return input ? Number(input.value) : 0;
    }

    async handleAddToCart() {
      const tier = this.getSelectedTier();
      const variantId = this.getVariantId();
      if (!variantId) {
        alert('Lütfen bir ürün seçeneği belirleyin.');
        return;
      }

      const items = [
        {
          id: variantId,
          quantity: tier.qty,
          properties: {
            _vellum_paket: tier.label,
            _vellum_qty: String(tier.qty),
            _vellum_discount_pct: String(tier.discount),
          },
        },
      ];

      // Cross-sell: Leather Journal — qty matches selected set count
      if (this.upsellCheckbox && this.upsellCheckbox.checked) {
        const upsellId = Number(this.upsellCheckbox.dataset.variantId || 0);
        const offerCents = Number(this.upsellCheckbox.dataset.offerCents || this.root.dataset.journalOfferCents || 29900);
        const parentId = this.root.dataset.penProductId || '';
        if (upsellId) {
          items.push({
            id: upsellId,
            quantity: Math.max(1, Number(tier.qty) || 1),
            properties: {
              _vellum_upsell: 'pdp-checkbox',
              _vellum_parent_id: String(parentId),
              _vellum_offer_cents: String(offerCents),
              _vellum_match_sets: '1',
            },
          });
        }
      }

      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

      try {
        await addItems(items);

        // Optional: apply quantity-tier discount codes (configure in Shopify Admin)
        const code = tier.code || this.discountCodes[tier.qty];
        if (code) {
          try {
            await updateCart({ discount: code });
          } catch (discountErr) {
            console.warn('[VELLUM] discount code not applied', discountErr);
          }
        }

        // Journal offer discount (Admin code → 299 TL)
        if (this.upsellCheckbox && this.upsellCheckbox.checked) {
          const journalCode = (this.root.dataset.journalDiscountCode || this.upsellCheckbox.dataset.discountCode || '').trim();
          if (journalCode) {
            try {
              await updateCart({ discount: journalCode });
            } catch (e) {
              console.warn('[VELLUM] journal discount', e);
            }
          }
        }

        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        window.location.href = cartUrl;
      } catch (err) {
        console.error(err);
        alert(err.message || 'Sepete eklenemedi');
      } finally {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
      }
    }
  }

  /* --------------------------------------------------------------------------
     Reviews filters + modal (UI only — no external app)
     -------------------------------------------------------------------------- */
  function initReviews(root) {
    const filters = root.querySelectorAll('[data-vellum-review-filter]');
    const cards = root.querySelectorAll('[data-vellum-review-card]');
    const modal = root.querySelector('[data-vellum-review-modal]');
    const openBtn = root.querySelector('[data-vellum-review-open]');

    filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.vellumReviewFilter;
        filters.forEach((b) => b.classList.toggle('is-active', b === btn));
        cards.forEach((card) => {
          const tags = (card.dataset.tags || '').split(/\s+/);
          const show = filter === 'all' || tags.includes(filter);
          card.classList.toggle('is-hidden', !show);
        });
      });
    });

    if (modal && openBtn) {
      const closeModal = () => {
        modal.classList.remove('is-open');
        document.documentElement.classList.remove('vellum-scroll-lock');
      };
      openBtn.addEventListener('click', () => {
        modal.classList.add('is-open');
        document.documentElement.classList.add('vellum-scroll-lock');
      });
      modal.querySelectorAll('[data-vellum-modal-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
      });
      const form = modal.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          // Simulation UI — wire to a form endpoint / metaobject later if needed
          alert('Teşekkürler! Yorumunuz incelemeye alındı.');
          form.reset();
          closeModal();
        });
      }
    }
  }

  /* --------------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------------- */
  function boot() {
    const drawerEl = document.querySelector('[data-vellum-cart-drawer]');
    if (drawerEl && !drawerEl._vellumMounted) {
      drawerEl._vellumMounted = true;
      window.VellumCartDrawer = new VellumCartDrawer(drawerEl);
    } else if (!window._vellumCartPageRedirect) {
      // No drawer: any open request goes to full cart page
      window._vellumCartPageRedirect = true;
      document.addEventListener('vellum:cart:open', () => {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        window.location.href = cartUrl;
      });
    }

    document.querySelectorAll('[data-vellum-pdp]').forEach((el) => {
      if (!el._vellumMounted) {
        el._vellumMounted = true;
        new VellumPdpBuyBox(el);
      }
    });

    document.querySelectorAll('[data-vellum-reviews]').forEach((el) => {
      if (!el._vellumMounted) {
        el._vellumMounted = true;
        initReviews(el);
      }
    });

    document.querySelectorAll('[data-vellum-viewer-count]').forEach((el) => {
      if (el._vellumMounted) return;
      el._vellumMounted = true;
      const tick = () => {
        const cur = parseInt(el.textContent, 10) || 17;
        const next = Math.max(12, Math.min(38, cur + (Math.floor(Math.random() * 5) - 2)));
        el.textContent = String(next);
      };
      setInterval(tick, 9000 + Math.random() * 4000);
    });
  }

  // Public API for other scripts
  window.VellumCart = {
    fetchCart,
    addItems,
    changeItem,
    updateCart,
    formatMoney,
    open: () => document.dispatchEvent(new CustomEvent('vellum:cart:open')),
    refresh: () => document.dispatchEvent(new CustomEvent('vellum:cart:refresh')),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
